<template>
    <div id="app">
        <div id="loader" v-show="store.viewLoader || !store.selfDataLoaded">
            <transition name="loader-transition">
                <div class="loader-bg">
                    <div class="loader-img"></div>
                </div>
            </transition>
        </div>

        <top-bar v-if="store.selfDataLoaded"></top-bar>

        <div id="router" v-if="store.selfDataLoaded">
            <transition name="router-transition">
                <router-view></router-view>
            </transition>
        </div>

        <transition-group id="alerts" tag="div" name="alert-transition">
            <div class="alert" v-for="(alert, key) in alerts" :key="key" :style=" {'background-color': alert.color}">
                <div class='alert-text' v-html="alert.text"></div>
                <div class="alert-cross" @click="removeAlert(key)"></div>
            </div>
        </transition-group>
    </div>
</template>

<style scoped lang="scss">
@import '../css/colors.scss';

#app {

    #loader {

        .loader-bg {
            position: fixed;
            z-index: 1002;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background: url('../images/loader-bg.png') repeat rgba(0, 0, 0, 0.7);

            .loader-img {
                margin-left: -32px;
                margin-top: -32px;
                position: absolute;
                top: 50%;
                left: 50%;
                text-indent: -9999em;
                border: 8px solid rgba(255, 255, 255, 0.5);
                border-left-color: #ffffff;
                -webkit-transform: translateZ(0);
                -ms-transform: translateZ(0);
                transform: translateZ(0);
                -webkit-animation: load8 1.1s infinite linear;
                animation: load8 1.1s infinite linear;
            }
            .loader-img,
            .loader-img:after {
                border-radius: 50%;
                width: 64px;
                height: 64px;
            }
            @keyframes load8 {
                0% {
                    -webkit-transform: rotate(0deg);
                    transform: rotate(0deg);
                }
                100% {
                    -webkit-transform: rotate(360deg);
                    transform: rotate(360deg);
                }
            }

            @-webkit-keyframes load8 {
                0% {
                    -webkit-transform: rotate(0deg);
                    transform: rotate(0deg);
                }
                100% {
                    -webkit-transform: rotate(360deg);
                    transform: rotate(360deg);
                }
            }
        }
        &.loader-transition-leave-active {
            transition: opacity 0.4s;
        }
        &.loader-transition-enter-active {
            transition: opacity 0.2s;
        }
        &.loader-transition-enter,
        &.loader-transition-leave-active {
            opacity: 0
        }
    }

    #router {
        >:first-child {
            padding: 1.5em 0.5em;
        }
        .router-transition-enter-active,
        .router-transition-leave-active {
          transition-property: opacity;
          transition-duration: .25s;
        }
        .router-transition-enter-active {
          transition-delay: .25s;
        }
        .router-transition-enter,
        .router-transition-leave-active {
          opacity: 0
        }
    }

    #alerts {
        position: fixed;
        z-index: 500;
        bottom: 0;
        width: 100%;

        .alert {
            position: relative;
            padding: 16px 30px;
            color: white;
            opacity: 0.75;

            &:not(:last-child) {
                margin-bottom: 1px;
            }
            .alert-text {
                padding-right: 30px;
            }
            .alert-cross {
                position: absolute;
                cursor: pointer;
                right: 20px;
                top: 14px;
                width: 22px;
                height: 22px;
                border: 0;
                background: url('../images/icon-cross.png') no-repeat;
            }
             &.alert-transition-enter {
                opacity: 0;
                transform: scaleY(0);
            }
            &.alert-transition-move,
            &.alert-transition-enter-active,
            &.alert-transition-leave-active {
                transition: all .4s cubic-bezier(0.5, 0.05, 0.5, 0.95);
            }
            &.alert-transition-leave-active {
                opacity: 0;
                transform: scaleY(0.1);
            }
        }
    }
}
</style>

<script>
import TopBar from './components/TopBar.vue';
import ApiMixin from './mixins/ApiMixin.js';

import {
    TIMEOUTS
} from './constants.js';

import {
    getCookie,
    setCookie
} from './utils.js';

export default {
    name: 'App',
    components: {
        TopBar,
    },
    mixins: [
        ApiMixin,
    ],
    data() {
        return {
            store: {
                /* Common data. */

                patient: null,
                facility: null,
                departmentsMap: {},
                departmentsList: [],
                freeTermsList: [],
                isPatientLoggedIn: false,
                maxPatientVisitSearchRange: 180,


                /* Common data but only those that affect App.vue. */

                selfDataLoaded: false,
                viewLoader: true,
                loaderTimeout: null,


                /* Component depended. */

                // 1. FreeTermsList.vue && FreeTerm.vue - guard which tells
                //    if there's already some free term chosen by user.
                'FreeTerm-hasSomeFreeTermBeenChosen': false,

                // 2. FreeTerm.vue && Login.vue && FreeTermConfirmation.vue - single freeTerm object
                //    which leads chosen free term from the FreeTermsList.vue component. Always when
                //    currently no freeTerm is being chosen, this variable should be set to null.
                'FreeTerm-chosenFreeTerm': null,

                // 3. NewVisit.vue - this variable is used as the NewVisit.vue component cache to save
                //    its state right before the route changes and load this state once again on return.
                'NewVisit-cache': null,
            },
            alerts: [],
        }
    },
    computed: {
        $facility() {
            // Returns id of current facility.
            return this.store.facility ? this.store.facility.id : null;
        },
    },
    methods: {
        /* Alerts. */

        showAlert(text, color, timeout=null) {
            this.alerts.push({
                text: text,
                color: color,
            });

            setTimeout(() => {
                this.removeAlert(this.alerts.indexOf(alert));
            }, timeout ? timeout : TIMEOUTS.ALERT);
        },
        removeAlert(index) {
            this.alerts.splice(index, 1)
        },

        showFailureAlert(text, timeout=null) {
            this.showAlert(text, 'red', timeout);
        },
        showSuccessAlert(text, timeout=null) {
            this.showAlert(text, 'green', timeout);
        },


        /* Loader. */

        showLoader() {
            if (this.store.loaderTimeout)
                clearTimeout(this.store.loaderTimeout);

            this.store.loaderTimeout = setTimeout(() => {
                this.store.viewLoader = true;
            }, TIMEOUTS.LOADER);
        },
        hideLoader() {
            clearTimeout(this.store.loaderTimeout);
            this.store.viewLoader = false;
        },


        /* Data fetching. */

        loadSelf(action=()=>{}) {
            // Returns promise.
            return this.$http.get(this.$api.self.list()).then(response => {
                this.store.patient = response.data.patient;
                this.store.facility = response.data.facility;
                this.store.maxPatientVisitSearchRange = response.data.facility.max_patient_visit_search_range;
                if (this.store.patient) this.store.isPatientLoggedIn = true;
                action();
            });
        },

        /* Default actions depending on a status code returned from API. */

        $defaultServerErrorAction() {
            this.hideLoader();
            this.$router.push({name: 'index'});
            this.showFailureAlert('Wystapił nieoczekiwany błąd systemu.');
        },
    },
    created() {
        // Load vital data first.
        this.loadSelf().then(() => {
            // Then load all facility's departments.
            this.$http.get(this.$api.facility.detailDepartments(this.$facility)).then(response => {
                this.store.departmentsList = response.data;

                response.data.forEach(elem => this.store.departmentsMap[elem.id] = elem);

                // Essential data loaded! Other components can do their own stuff from now on...
                this.store.selfDataLoaded = true;
                this.$app.hideLoader();
            });
        });
    },
    mounted() {
        /* Handle all events sending by the event bus. */
        this.$bus.$on('$store:setFreeTerm-hasSomeFreeTermBeenChosen', value => {
            this.store['FreeTerm-hasSomeFreeTermBeenChosen'] = value;
        });

        this.$bus.$on('$store:setFreeTerm-chosenFreeTerm', value => {
            this.store['FreeTerm-chosenFreeTerm'] = value;
        });

        this.$bus.$on('$store:setNewVisit-cache', value => {
            this.store['NewVisit-cache'] = value;
        });

        this.$bus.$on('$store:setFreeTermsList', value => {
            this.store.freeTermsList = value;
        });

        this.$bus.$on('$store:setPatient', value => {
            this.store.patient = value;
        });

        this.$bus.$on('$store:setIsPatientLoggedIn', value => {
            this.store.isPatientLoggedIn = value;
        });
    },
}
</script>



// WEBPACK FOOTER //
// App.vue?3621db93